import { useMutation, useQuery } from "@tanstack/react-query";
import { useContext, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  createBookAppointment,
  getAppointmentServices,
  getHfLocation,
  getServiceOfferingOption,
  getUserAddress,
  getVaccineOffering,
  getVaccineTabs,
  payForAppointment,
  getAppointments,
} from "service";
import { useModal } from "./useModal";
import {
  decryptData,
  encryptData,
  saveTokenToLocalStore,
  secretKey,
} from "utils";
import { toast } from "sonner";
import { BookAppointmentCartContext } from "context/book-appointment-cart";

type ITabs = "immunization" | "my-health" | "lab-test" | "consultation" | "";

export interface IAppointmentService {
  id: number;
  name: string;
  slug: ITabs;
}

export interface IAppointmentServiceResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IAppointmentService[];
}

export interface IAppointmentLocationResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IAppointementLocation[];
}

export interface IAppointementLocation {
  id: number;
  name_of_facility: string;
  address: string;
  email: string;
  phone: string;
  opening_hour: string;
  closing_hour: string;
  logo: string;
  is_active: boolean;
  location: string;
  lga: string;
  service_type: string;
  home_service_fee: string;
}

export interface IHfLocationReponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IHfLocation[];
}

export interface IHfLocation {
  id: number;
  logo: string | null;
  name_of_facility: string;
  address: string;
}

export interface IAddresses {
  id: number;
  address: string;
  state: string;
  lga: string;
  bus_stop: string;
  active: boolean;
}

export interface IAddressesResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IAddresses[];
}

export interface IVaccineTabs {
  name: string;
  slug: string;
}

export interface IVaccineTabsResponse {
  name: string;
  slug: string;
  sub_services: IVaccineTabs[];
}

export interface IVaccineOfferingResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IVaccineOffering[];
}

export interface IVaccineOffering {
  id: number;
  name: string;
  image: string;
  description: string;
  service: string;
}
export interface IVaccinesOfferingOptions {
  id: number;
  brand: {
    id: number;
    name: string;
    dosage_information: string;
    shots: number;
  };
  cost: string;
}

export interface IVaccinesOfferingOptionResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IVaccinesOfferingOptions[];
}

export interface IPayForAppointmentResponse {
  appointment: number;
  paid: boolean;
  amount: string;
  location_details: string;
}

//! new
interface IHealthFacility {
  id: number;
  name_of_facility: string;
  location: number;
  lga: string | null;
  address: string;
}

export interface IHomeDetails {
  // Define based on your home details structure
  address?: string;
  [key: string]: any;
}

export interface IAppointment {
  id: number;
  health_facility: IHealthFacility | null;
  home_details: IHomeDetails | null;
  virtual_link: string | null;
  book_date: string;
  book_time: string;
  status: string;
  paid: boolean;
  assigned_to: any | null;
}

export interface IAppointmentResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: IAppointment[];
}

const APPOINTMENT_DATA_KEY = "appointment_data";

//! initialize appointment data (with health facility)
export const initAppointmentData = (key: string, value: any) => {
  if (sessionStorage.getItem("cart")) {
    return "cart";
  }

  let existingData: Record<string, any> = {};

  const encrypted = sessionStorage.getItem(APPOINTMENT_DATA_KEY);
  if (encrypted) {
    try {
      existingData = decryptData(encrypted, secretKey) || {};
    } catch (err) {
      console.error("Failed to decrypt existing appointment data:", err);
    }
  }

  const updatedData = {
    ...existingData,
    [key]: value,
  };

  sessionStorage.setItem(
    APPOINTMENT_DATA_KEY,
    encryptData(updatedData, secretKey)
  );
};

//! get appointment data
export const getAppointmentData = () => {
  try {
    const encrypted = sessionStorage.getItem(APPOINTMENT_DATA_KEY);
    if (!encrypted) return null;
    return decryptData(encrypted, secretKey) as any;
  } catch (err) {
    console.error("Failed to get appointment data:", err);
    return null;
  }
};

//! get cart data
export const getCartData = () => {
  try {
    const encrypted = sessionStorage.getItem("cart");
    if (!encrypted) return null;
    return decryptData(encrypted, secretKey) as any;
  } catch (err) {
    console.error("Failed to get appointment data:", err);
    return null;
  }
};

//! add to cart data
export const addToCartData = (currData: any) => {
  const existingData = getCartData();
  if (!existingData) {
    sessionStorage.setItem("cart", encryptData([currData], secretKey)); // <-- wrap in array
  } else {
    const merged = [...existingData, currData];
    sessionStorage.setItem("cart", encryptData(merged, secretKey));
  }
};

//! get curr data
export const getCurrData = () => {
  try {
    const encrypted = sessionStorage.getItem("curr");
    if (!encrypted) return null;
    return decryptData(encrypted, secretKey) as any;
  } catch (err) {
    console.error("Failed to get appointment data:", err);
    return null;
  }
};

//! add to curr data
export const addToCurrData = (key: string, value: any) => {
  const existingData = getCurrData();

  if (!existingData) {
    sessionStorage.setItem("curr", encryptData({ [key]: value }, secretKey));
  } else {
    const merged = { ...existingData, [key]: value };
    saveTokenToLocalStore("curr", merged);
  }
};

//! add to appointment data
export const addToAppointmentData = (key: string, value: any) => {
  const existingData = getAppointmentData();

  const merged = { ...existingData, [key]: value };
  saveTokenToLocalStore(APPOINTMENT_DATA_KEY, merged);
};

//! clear curr data
export const clearCurrData = () => {
  sessionStorage.removeItem("curr");
};

//! clear appointment data and cart data
export const clearAppointmentData = () => {
  sessionStorage.removeItem(APPOINTMENT_DATA_KEY);
  sessionStorage.removeItem("cart");
};

//! edit appointment data
export const editAppointmentData = (id: number) => {};

const useBookAppointment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedVaccineTab, setSelectedVaccineTab] = useState(
    "adulthood-other-catchup-immunization"
  );
  const [selectedAddress, setSelectedAddress] = useState(0);
  const [showAddForm, setShowAddForm] = useState(false);
  const { vaccineBrand } = useParams();
  const [selectedVaccine, setSelectedVaccine] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [showDosageInfo, setShowDosageInfo] = useState<number | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");
  const [walletPin, setWalletPin] = useState("");

  const {
    isOpen: isWarningOpen,
    openModal: openWarningModal,
    closeModal: closeWarningModal,
  } = useModal();

  const {
    isOpen: isSelectAccountOpen,
    openModal: openSelectAccountModal,
    closeModal: closeSelectAccountModal,
  } = useModal();

  const {
    isOpen: isAmountOpen,
    openModal: openAmountModal,
    closeModal: closeAmountModal,
  } = useModal();

  const {
    isOpen: isPasswordModalOpen,
    openModal: openPasswordModal,
    closeModal: closePasswordModal,
  } = useModal();

  const {
    isOpen: isTwoFactorModalOpen,
    openModal: openTwoFactorModal,
    closeModal: closeTwoFactorModal,
  } = useModal();

  const {
    isOpen: isInsufficientBalanceOpen,
    openModal: openInsufficientBalanceModal,
    closeModal: closeInsufficientBalanceModal,
  } = useModal();

  const { dispatch } = useContext(BookAppointmentCartContext);

  const handleAddAddress = () => {
    setShowAddForm(true);
  };

  const handleCloseAddAddress = () => {
    setShowAddForm(false);
  };

  const handlePaymentSelect = (id: string) => {
    setSelectedPaymentMethod(id);
  };

  const formatPrice = (price: string) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(parseFloat(price));
  };

  // get appointment services
  const {
    data: appointmentServicesResult,
    isLoading: isAppointmentServicesLoading,
  } = useQuery<IAppointmentServiceResponse>({
    queryKey: ["appointment-services"],
    queryFn: async () => {
      const response = await getAppointmentServices();
      return response;
    },
  });

  const appointmentServices = appointmentServicesResult?.results || [];

  //! get health facility locations (to remove)
  const { data: healthFacilities, isLoading: isHealthfacilitiesLoading } =
    useQuery<IHfLocationReponse>({
      queryKey: ["health-facilities"],
      queryFn: async () => {
        const response = await getHfLocation(1);
        return response;
      },
    });

  const healthFacilitiesOptions = healthFacilities?.results || [];

  // get user address
  const { data: userAddress, isLoading: userAddressLoading } =
    useQuery<IAddressesResponse>({
      queryKey: ["user-address"],
      queryFn: async () => {
        const response = await getUserAddress();
        return response;
      },
    });

  const addresses = userAddress?.results || [];

  // get active tab
  const getActiveTab = () => {
    if (location.pathname.includes("vaccines")) {
      return "immunization";
    }
    if (location.pathname.includes("my-health")) {
      return "my-health";
    }
    if (location.pathname.includes("lab-test")) {
      return "lab-test";
    }
    if (location.pathname.includes("consultation")) {
      return "consultation";
    }
    return "";
  };

  //! get vaccine tabs
  const { data: vaccineTabsResult, isLoading: isVaccineTabsLoading } =
    useQuery<IVaccineTabsResponse>({
      queryKey: ["vaccines", getActiveTab()],
      queryFn: async () => {
        if (!getActiveTab()) return;
        const response = await getVaccineTabs(getActiveTab());
        return response;
      },
    });

  const vaccineTabs = vaccineTabsResult?.sub_services || [];

  // get vaccine offering
  const { data: vaccineOfferingResult, isLoading: isVaccineOfferingLoading } =
    useQuery<IVaccineOfferingResponse>({
      queryKey: ["vaccines", selectedVaccineTab],
      queryFn: async () => {
        if (!selectedVaccineTab) return;
        const response = await getVaccineOffering(selectedVaccineTab);
        return response;
      },
    });

  const vaccineOfferingSubServices = vaccineOfferingResult?.results || [];

  //! get vaccine offering options
  const {
    data: vaccineOfferingOptionsResult,
    isLoading: isVaccineOfferingOptionsLoading,
  } = useQuery({
    queryKey: ["vaccines", vaccineBrand],
    queryFn: async () => {
      const response = await getServiceOfferingOption(vaccineBrand);
      return response;
    },
    enabled: !!vaccineBrand,
  });

  const vaccineOfferingOptions = vaccineOfferingOptionsResult?.results || [];

  //! post book appointment
  const { mutate: bookAppointment, isPending: isBookingAppointment } =
    useMutation({
      mutationFn: async (formData: any) => {
        const transformedData = {
          health_facility: formData.health_facility,
          book_date: formData.book_date,
          book_time: formData.book_time.slice(0, 5),
          appointment_items: formData.appointment_items.map((item: any) => ({
            service_offering_option: item.service_offering_option,
            appointmentitembookedfor: item.appointmentitembookedfor.map(
              (person: any) => ({
                family_member: person.family_member,
                quantity: person.quantity,
              })
            ),
          })),
        };

        const payload: any = {
          ...transformedData,
        };
        const response = await createBookAppointment(payload);
        return response;
      },
      onSuccess: (data) => {
        clearAppointmentData();
        toast.success("Appointment created successfully");

        clearAppointmentData();
        dispatch({ type: "CLEAR_CART" });

        navigate(`/booking-summary/booking-payment/${data.id}`);
      },
      onError: (error) => {
        console.error("Mutation error:", error);
        toast.error("Failed to create appointment");
      },
    });

  //! pay for appointment
  const {
    mutate: makeAppointmentPayment,
    isPending: isMakeAppointmentPaymentLoading,
  } = useMutation({
    mutationFn: async (formaData: { appointment: number; pin: string }) => {
      const response = await payForAppointment(
        formaData.appointment,
        formaData.pin
      );

      return response;
    },
    onSuccess: (data: IPayForAppointmentResponse) => {
      if (data.paid) {
        toast.success("Payment successful");
        navigate("/booking-summary/booking-successful");
      } else {
        toast.error("Payment failed");
      }
    },
  });

  //! get appointments
  const { data: appointments, isLoading: isAppointmentsLoading } =
    useQuery<IAppointmentResponse>({
      queryKey: ["appointments"],
      queryFn: async () => {
        const response = await getAppointments();
        return response;
      },
    });

  return {
    appointmentServices,
    isAppointmentServicesLoading,
    navigate,
    location,
    selectedAddress,
    setSelectedAddress,
    showAddForm,
    setShowAddForm,
    addresses,
    handleAddAddress,
    handleCloseAddAddress,
    userAddressLoading,
    vaccineTabs,
    vaccineOfferingSubServices,
    isVaccineOfferingLoading,
    isVaccineTabsLoading,
    selectedVaccineTab,
    setSelectedVaccineTab,
    vaccineOfferingOptionsResult,
    isVaccineOfferingOptionsLoading,
    vaccineBrand,
    vaccineOfferingOptions,
    openSelectAccountModal,
    closeSelectAccountModal,
    isSelectAccountOpen,
    formatPrice,
    selectedVaccine,
    setSelectedVaccine,
    showDosageInfo,
    setShowDosageInfo,
    healthFacilitiesOptions,
    isHealthfacilitiesLoading,
    bookAppointment,
    isBookingAppointment,
    isWarningOpen,
    openWarningModal,
    closeWarningModal,
    isAmountOpen,
    openAmountModal,
    closeAmountModal,
    isPasswordModalOpen,
    openPasswordModal,
    closePasswordModal,
    isTwoFactorModalOpen,
    openTwoFactorModal,
    closeTwoFactorModal,
    isInsufficientBalanceOpen,
    openInsufficientBalanceModal,
    closeInsufficientBalanceModal,
    handlePaymentSelect,
    selectedPaymentMethod,
    makeAppointmentPayment,
    isMakeAppointmentPaymentLoading,
    walletPin,
    setWalletPin,
    appointments,
    isAppointmentsLoading,
  };
};

export default useBookAppointment;
